import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './useAuth';
import { INITIAL_STATE } from './usePurchaseState';
import * as service from '@/supabase/messagetemplate/service/messageTemplateService';
import { logError } from '@/lib/sentry';
import type { MessageTemplate } from '@/types/messageTemplate';

const DEFAULT_TEMPLATE_NAME = '기본 안내';
// 신규 가입자 / 백필 누락 사용자에게 lazy-create되는 default 템플릿 본문.
// 기존 신규 가입자가 받던 템플릿(usePurchaseState INITIAL_STATE)과 동일하게 유지.
const DEFAULT_TEMPLATE_CONTENT = INITIAL_STATE.memberSetting.messageTemplate;

const MAX_TEMPLATES = 3;

const activeTemplateKey = (userId: string) => `active_template_id_${userId}`;

export interface UseMessageTemplatesResult {
  templates: MessageTemplate[];
  activeTemplate: MessageTemplate | null;
  setActiveTemplateId: (id: number) => void;
  isLoading: boolean;
  isAtMaxLimit: boolean;
  isAtMinLimit: boolean;
  createTemplate: (name: string, content: string) => Promise<MessageTemplate | null>;
  updateActiveTemplate: (updates: { name?: string; content?: string }) => Promise<void>;
  deleteActiveTemplate: () => Promise<void>;
  reload: () => Promise<void>;
}

export function useMessageTemplates(): UseMessageTemplatesResult {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // loadTemplates 전용 stale-load guard. CRUD와 분리 — CRUD가 generation을 건드리면
  // 동시 same-user 저장을 부당하게 suppress하므로 절대 같이 쓰지 말 것.
  const generationRef = useRef(0);
  // Lazy-create 직렬화 가드: concurrent loadTemplates 시 둘 다 SELECT 빈 결과 → 둘 다 INSERT
  // 발생을 막기 위해, 이전 in-flight load가 끝날 때까지 다음 load는 기다린다.
  // 첫 INSERT가 commit된 후에 두 번째 load의 SELECT가 비지 않으므로 lazy-create 1회만 발생.
  const inFlightPromiseRef = useRef<Promise<void> | null>(null);
  // CRUD가 auth user 변경을 감지하기 위한 ref. user는 useCallback closure에 capture되므로
  // 직접 비교 시 stale. ref로 always-fresh user를 읽어 myUserId와 비교.
  // ⚠️ 반드시 render 동안 sync 할당 — useEffect는 paint 후 실행되어 그 사이 도착한
  // stale 응답이 OLD user에 setState 적용하는 privacy 윈도우가 생김.
  const userRef = useRef(user);
  userRef.current = user;
  // 마찬가지로 templates도 always-fresh로 읽기 위한 ref. CRUD가 await 후 templates를
  // closure capture로 읽으면 그 사이 다른 CRUD가 setTemplates한 값이 stale이 됨.
  const templatesRef = useRef(templates);
  templatesRef.current = templates;
  // 마지막으로 load 완료된 user.id. user 전환 시 즉시 state를 clear하여 OLD user의
  // 데이터가 NEW user에게 잠시 노출되지 않도록.
  const loadedUserIdRef = useRef<string | null>(null);
  // updateActiveTemplate 직렬화 큐. 네트워크가 느려 v1이 in-flight인 동안 v2가 발송되면
  // v2가 먼저 commit하고 v1이 늦게 commit하여 DB가 v1로 stale해지는 race를 막음.
  // 각 update가 이전 update의 완료를 기다린 후 자신의 PUT을 보내도록.
  const updateChainRef = useRef<Promise<unknown>>(Promise.resolve());

  const loadTemplates = useCallback(async () => {
    // generation을 IMMEDIATELY 증가시켜 진행 중인 stale 응답을 즉시 invalidate.
    // (await 후에 증가시키면 wait 동안 도착한 stale CRUD 응답이 새 user에 적용될 수 있음)
    const myGeneration = ++generationRef.current;

    // user.id가 바뀌었다면 IMMEDIATELY state clear — OLD user의 templates가
    // NEW user 세션에 잠시 노출되는 윈도우 차단. (await 후엔 너무 늦음)
    const newUserId = user?.id ?? null;
    if (newUserId !== loadedUserIdRef.current) {
      setTemplates([]);
      setActiveId(null);
    }

    // 직렬화: 진행 중인 load가 있으면 끝난 뒤 진행. 이미 gen은 증가됐으므로
    // 그동안 도착한 모든 stale 응답은 invalidate된 상태.
    if (inFlightPromiseRef.current) {
      try { await inFlightPromiseRef.current; } catch { /* ignore */ }
    }
    if (myGeneration !== generationRef.current) return; // 더 새로운 load가 시작됨

    if (!user) {
      setIsLoading(false);
      loadedUserIdRef.current = null;
      return;
    }

    const myUserId = user.id;
    setIsLoading(true);

    const work = (async () => {
      try {
        let list = await service.getMessageTemplates(myUserId);
        if (myGeneration !== generationRef.current) return; // stale

        // Lazy 생성: 결과가 비어있으면 default 1행 INSERT 후 재조회.
        // 직렬화 가드 덕에 동시 호출 시 두 번째는 첫 번째의 INSERT 후 SELECT를 보므로
        // 비지 않아 다시 INSERT하지 않음.
        if (list.length === 0) {
          if (myGeneration !== generationRef.current) return;
          await service.createMessageTemplate(myUserId, {
            name: DEFAULT_TEMPLATE_NAME,
            content: DEFAULT_TEMPLATE_CONTENT,
            sortOrder: 0,
          });
          if (myGeneration !== generationRef.current) return;
          list = await service.getMessageTemplates(myUserId);
          if (myGeneration !== generationRef.current) return;
        }

        setTemplates(list);

        // 활성 templateId 결정: localStorage 우선, fallback은 첫 번째
        const stored = localStorage.getItem(activeTemplateKey(myUserId));
        const storedId = stored ? Number(stored) : NaN;
        const exists = list.some(t => t.id === storedId);
        setActiveId(exists ? storedId : (list[0]?.id ?? null));

        loadedUserIdRef.current = myUserId;
      } catch (e) {
        if (myGeneration !== generationRef.current) return;
        logError('Failed to load message templates', e);
      } finally {
        if (myGeneration === generationRef.current) {
          setIsLoading(false);
        }
      }
    })();

    // work를 ref에 저장 (.finally로 cleanup 시 같은 ref면 null로 reset).
    // ref에 work 자체를 저장하므로 finally 콜백 내부 비교가 정확히 작동.
    inFlightPromiseRef.current = work;
    work.finally(() => {
      if (inFlightPromiseRef.current === work) {
        inFlightPromiseRef.current = null;
      }
    });
    await work;
  }, [user]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const setActiveTemplateId = useCallback((id: number) => {
    if (!user) return;
    setActiveId(id);
    localStorage.setItem(activeTemplateKey(user.id), String(id));
  }, [user]);

  const createTemplate = useCallback(async (name: string, content: string): Promise<MessageTemplate | null> => {
    if (!user) return null;
    if (templatesRef.current.length >= MAX_TEMPLATES) return null;
    const myUserId = user.id;
    try {
      const maxSort = templatesRef.current.reduce((m, t) => Math.max(m, t.sortOrder), -1);
      const created = await service.createMessageTemplate(myUserId, {
        name,
        content,
        sortOrder: maxSort + 1,
      });
      // auth user가 변경되었다면 새 user 세션에 setState 적용하지 않음 (privacy).
      if (userRef.current?.id !== myUserId) return null;
      // Functional update + idempotent append (concurrent delete가 templates를 줄였어도
      // 옛 닿배열에 의존하지 않고, 이미 있는 row면 중복 추가 안 함).
      setTemplates(prev => prev.some(t => t.id === created.id) ? prev : [...prev, created]);
      setActiveId(created.id);
      localStorage.setItem(activeTemplateKey(myUserId), String(created.id));
      return created;
    } catch (e) {
      logError('Failed to create template', e);
      return null;
    }
  }, [user]);

  const updateActiveTemplate = useCallback(async (updates: { name?: string; content?: string }) => {
    if (!user || activeId == null) return;
    const myUserId = user.id;
    const myActiveId = activeId;
    // 직렬화: 이전 update가 끝날 때까지 기다린 후 자신의 PUT을 보냄.
    // 이렇게 하면 server commit 순서 = 사용자 편집 순서가 되어 stale 덮어쓰기 방지.
    const previous = updateChainRef.current;
    const work = (async () => {
      try { await previous; } catch { /* 이전 실패는 무시 — 이번 시도는 별개 */ }
      const updated = await service.updateMessageTemplate(myUserId, myActiveId, updates);
      // auth 변경 시에만 차단 (privacy).
      if (userRef.current?.id !== myUserId) return;
      // myActiveId 기준으로 update — current activeId가 바뀌었어도 OLD 템플릿에 정확히 적용됨
      setTemplates(prev => prev.map(t => (t.id === myActiveId ? updated : t)));
      // 옛 컬럼 mirror는 별도 useEffect가 처리.
    })();
    // 다음 update가 await할 promise를 ref에 저장 (실패해도 chain 깨지지 않게 catch).
    updateChainRef.current = work.catch(() => {});
    try {
      await work;
    } catch (e) {
      logError('Failed to update active template', e);
      throw e;
    }
  }, [user, activeId]);

  const deleteActiveTemplate = useCallback(async () => {
    if (!user || activeId == null) return;
    if (templatesRef.current.length <= 1) return; // 최소 1개 보호 (DB 트리거 + 앱 가드 이중)
    const myUserId = user.id;
    const myActiveId = activeId;
    try {
      await service.deleteMessageTemplate(myUserId, myActiveId);
      if (userRef.current?.id !== myUserId) return;
      // 동시 create/delete race 회피: ref 또는 closure capture는 React render 시점에만
      // 갱신되므로 setTemplates 후 즉시 fresh가 아님. server에서 최종 상태를 다시
      // 읽어오는 것이 모든 동시 변경을 일관되게 반영.
      const fresh = await service.getMessageTemplates(myUserId);
      if (userRef.current?.id !== myUserId) return;
      setTemplates(fresh);
      const newActive = fresh[0]?.id ?? null;
      setActiveId(newActive);
      if (newActive != null) {
        localStorage.setItem(activeTemplateKey(myUserId), String(newActive));
      }
    } catch (e) {
      logError('Failed to delete active template', e);
      throw e;
    }
  }, [user, activeId]);

  // Dual-write: templates 또는 activeId가 변할 때마다 "현재 활성" 템플릿 content를 옛 컬럼에 미러링.
  // 명시 호출 대신 이 effect가 단일 source of truth — stale 응답이 옛 컬럼을 잘못된 content로
  // 덮어쓰는 race를 방지. 효과: state가 settle된 후 항상 current active로 수렴.
  useEffect(() => {
    if (!user || activeId == null) return;
    const active = templates.find(t => t.id === activeId);
    if (!active) return;
    service.mirrorActiveTemplateToLegacyColumn(user.id, active.content).catch(() => {});
  }, [activeId, templates, user]);

  const activeTemplate = templates.find(t => t.id === activeId) ?? null;

  return {
    templates,
    activeTemplate,
    setActiveTemplateId,
    isLoading,
    isAtMaxLimit: templates.length >= MAX_TEMPLATES,
    isAtMinLimit: templates.length <= 1,
    createTemplate,
    updateActiveTemplate,
    deleteActiveTemplate,
    reload: loadTemplates,
  };
}
